import moment from 'moment-timezone'
import 'moment/locale/fr'

// The words of the public coach pages, kept out of the components so they can
// be tested without rendering anything.
//
// Every time is printed in the club's timezone, not the reader's: a séance at
// 18:00 in Montpellier is at 18:00 for whoever reads about it, wherever they
// read it from. The server sends the zone with the club.
//
// The int64 fields arrive as strings - grpc-gateway's JSON writes a 64-bit
// integer as one - so every timestamp goes through Number first.

const DEFAULT_TZ = 'Europe/Paris'

function at(timestamp, timezone) {
    return moment.unix(Number(timestamp)).tz(timezone || DEFAULT_TZ).locale('fr')
}

function capitalise(text) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

export function durationLabel(minutes) {
    const m = Number(minutes) || 0
    const hours = Math.floor(m / 60)
    const rest = m % 60
    if (hours === 0) return `${rest} min`
    if (rest === 0) return `${hours}h`
    return `${hours}h${String(rest).padStart(2, '0')}`
}

// "Chaque mardi à 18:00 · 1h30" for a récurrence, "Samedi 14 novembre à 10:00
// · 1h" for a one-off.
export function scheduleLabel(series) {
    const timezone = series.club && series.club.timezone
    const start = at(series.timestamp_start, timezone)
    const duration = durationLabel(series.duration_minutes)

    if (series.repeat === 'LESSON_REPEAT_WEEKLY') {
        return `Chaque ${start.format('dddd')} à ${start.format('HH:mm')} · ${duration}`
    }
    return `${capitalise(start.format('dddd D MMMM'))} à ${start.format('HH:mm')} · ${duration}`
}

export function seanceLabel(lesson, timezone) {
    return capitalise(at(lesson.timestamp_start, timezone).format('ddd D MMM · HH:mm'))
}

export function kindLabel(kind) {
    return kind === 'LESSON_KIND_INDIVIDUAL' ? 'Cours individuel' : 'Cours collectif'
}

// The coach's own name for the créneau when they gave one.
export function seriesTitle(series) {
    return (series.label && series.label.trim()) || kindLabel(series.kind)
}

export function levelLabel(series) {
    const min = Number(series.level_min) || 0
    const max = Number(series.level_max) || 0
    if (min === 0 && max === 0) return null
    if (min === max || max === 0) return `Niveau ${min || max}`
    if (min === 0) return `Niveau ${max}`
    return `Niveau ${min} à ${max}`
}

export function priceLabel(series) {
    const cents = Number(series.price_cents) || 0
    if (cents === 0) return null
    const formatted = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: series.currency || 'EUR',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100)
    return `${formatted} la séance`
}

// What is left in one séance. Null for a séance with no stated capacity,
// which says nothing rather than something wrong.
export function placesLeft(lesson) {
    const capacity = Number(lesson.capacity) || 0
    if (capacity === 0) return null
    return Math.max(0, capacity - (Number(lesson.num_enrolled) || 0))
}

export function placesLabel(lesson) {
    const left = placesLeft(lesson)
    if (left === null) return null
    if (left === 0) return 'Complet'
    return left === 1 ? '1 place' : `${left} places`
}

export function coachName(coach) {
    return (coach.full_name && coach.full_name.trim()) || coach.username
}

export function clubsLabel(clubs) {
    return (clubs || []).map(club => club.ville ? `${club.name} (${club.ville})` : club.name).join(' · ')
}

export function openCountLabel(count) {
    const n = Number(count) || 0
    if (n === 0) return null
    return n === 1 ? '1 créneau' : `${n} créneaux`
}
